import React, { useState, useEffect } from 'react';
import { isRunningAsPWA, showInstallPrompt, isIOS, isSchoolRoute, setPWAInstallContext } from '../../utils/pwa-utils';
import { styled } from '@mui/material/styles';
import { IconButton, Snackbar, Box, Typography, Paper } from '@mui/material';
import { ButtonSubmitGreen, ButtonOutlineGreenWithDiffStyle } from './Buttons/Buttons';
import CloseIcon from '@mui/icons-material/Close';
import GetAppIcon from '@mui/icons-material/GetApp';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[3],
  position: 'fixed',
  bottom: 200,
  left: theme.spacing(2),
  right: theme.spacing(2),
  zIndex: 1000,
  [theme.breakpoints.up('sm')]: {
    width: 400,
    left: 'auto',
    right: theme.spacing(2),
  },
}));

const PWAInstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  useEffect(() => {
    if (isSchoolRoute()) return undefined;
    // Only show the install prompt if not already running as PWA
    if (!isRunningAsPWA()) {
      // Listen for the custom event that indicates PWA installation is available
      const handlePWAAvailable = () => {
        setShowPrompt(true);
      };

      window.addEventListener('pwaInstallAvailable', handlePWAAvailable);

      // Check if we're on iOS to show special instructions
      if (isIOS()) {
        // On iOS, we need to show manual instructions
        // Wait a bit before showing to not overwhelm the user immediately
        const timer = setTimeout(() => {
          setShowIOSInstructions(true);
        }, 5000);
        
        return () => {
          clearTimeout(timer);
          window.removeEventListener('pwaInstallAvailable', handlePWAAvailable);
        };
      }

      return () => {
        window.removeEventListener('pwaInstallAvailable', handlePWAAvailable);
      };
    }
  }, []);

  const handleInstallClick = async () => {
    setPWAInstallContext('main');
    await showInstallPrompt();
    // The browser prompt can only be answered once, win or lose - hide the prompt either way.
    setShowPrompt(false);
  };

  const handleClose = () => {
    setShowPrompt(false);
    setShowIOSInstructions(false);
  };

  // Don't render anything if running as PWA
  if (isRunningAsPWA() || isSchoolRoute()) {
    return null;
  }

  // For iOS devices, show special instructions
  if (showIOSInstructions) {
    return (
      <StyledPaper>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 1 }}>
          <Typography variant="h6" component="div">
            <strong>Install Bubble Ai</strong>
          </Typography>
          <IconButton size="small" onClick={handleClose} aria-label="close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        
        <Typography variant="body2" sx={{ mb: 1 }}>
          To install Bubble Ai on your iOS device:
        </Typography>
        
        <Box sx={{ width: '100%', mb: 1 }}>
          <Typography variant="body2" component="div">
            1. Tap the share icon <span style={{ fontSize: '1.2em' }}>⎙</span> at the bottom of your screen
          </Typography>
          <Typography variant="body2" component="div">
            2. Scroll down and tap "Add to Home Screen"
          </Typography>
          <Typography variant="body2" component="div">
            3. Then tap "Add" in the top right corner
          </Typography>
        </Box>
        
        <div style={{ width: "100%" }}>
            <ButtonOutlineGreenWithDiffStyle 
                type="button"
                onClick={handleClose}
            >
                <span>Got it</span>
            </ButtonOutlineGreenWithDiffStyle>
        </div>
      </StyledPaper>
    );
  }

  // For other browsers that support the install prompt
  if (showPrompt) {
    return (
      <Snackbar
        open={showPrompt}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <StyledPaper>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 1 }}>
            <Typography variant="h6" component="div">
              <strong>Install Bubble Ai</strong>
            </Typography>
            <IconButton size="small" onClick={handleClose} aria-label="close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          
          <Typography variant="body2" sx={{ mb: 1 }}>
            Install Bubble Ai for a better experience with offline access and faster loading times.
          </Typography>
          

            <div style={{ width: "100%" }}>
                <ButtonSubmitGreen 
                    type="button"
                    onClick={handleInstallClick}
                >
                    <GetAppIcon fontSize='medium' /><span style={{ marginLeft: '5px', addingTop: "1px" }}>Install App</span>
                </ButtonSubmitGreen>
            </div>
        </StyledPaper>
      </Snackbar>
    );
  }

  return null;
};

export default PWAInstallPrompt;
